// Frontend/src/api/subscriptions.ts
import api from "./client";

export type BillingCycle = "monthly" | "quarterly" | "annual";

export type SubscriptionPlan = {
  id: string;
  name: string;
  price_usd: string; // decimal string
  billing_cycle: BillingCycle;
  max_units: number;
  description: string;
  is_active: boolean;
  subscription_count: number;
  created_at: string;
  updated_at: string;
};

/** Lightweight plan for HOA admin self-service (no internal counts) */
export type PublicSubscriptionPlan = {
  id: string;
  name: string;
  price_usd: string;
  billing_cycle: BillingCycle;
  max_units: number;
  description: string;
};

export type SubscriptionStatus = "trial" | "active" | "suspended" | "cancelled" | "expired";

export type HOASubscription = {
  id: string;
  hoa_admin: string;
  hoa_admin_email: string;
  hoa_admin_name: string;
  organization_name: string | null;
  plan: string | null;
  plan_detail: SubscriptionPlan | null;
  status: SubscriptionStatus;
  started_at: string;
  trial_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancelled_at: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
};

export type SubscriptionInvoiceStatus =
  | "PENDING_PAYMENT"
  | "PENDING_VERIFICATION"
  | "COMPLETED"
  | "REJECTED"
  | "CANCELLED";

export type SubscriptionPaymentMethod = "BANK_TRANSFER" | "ZELLE" | "STRIPE" | "PAGO_MOVIL" | "CASH";

export type SubscriptionInvoice = {
  id: string;
  hoa_admin: string;
  hoa_admin_email: string;
  plan: string;
  plan_detail: PublicSubscriptionPlan | null;
  amount_usd: string;
  billing_cycle: BillingCycle;
  status: SubscriptionInvoiceStatus;
  payment_method: SubscriptionPaymentMethod | null;
  payment_proof: string | null;
  payment_proof_url: string | null;
  notes: string;
  verified_at: string | null;
  verified_by: string | null;
  rejection_reason: string;
  created_at: string;
  updated_at: string;
};

export type CreatePlanData = {
  name: string;
  price_usd: number;
  billing_cycle: BillingCycle;
  max_units: number;
  description?: string;
  is_active?: boolean;
};

export type UpdatePlanData = Partial<CreatePlanData>;

export type UpdateSubscriptionData = {
  plan?: string | null;
  status?: SubscriptionStatus;
  trial_ends_at?: string | null;
  current_period_start?: string | null;
  current_period_end?: string | null;
  notes?: string;
};

// ── Super-admin: Plans ────────────────────────────────────────────────────────

export async function listPlans(): Promise<SubscriptionPlan[]> {
  const { data } = await api.get<SubscriptionPlan[]>("/platform/billing/plans/");
  return data;
}

export async function createPlan(payload: CreatePlanData): Promise<SubscriptionPlan> {
  const { data } = await api.post<SubscriptionPlan>("/platform/billing/plans/", payload);
  return data;
}

export async function updatePlan(id: string, payload: UpdatePlanData): Promise<SubscriptionPlan> {
  const { data } = await api.patch<SubscriptionPlan>(`/platform/billing/plans/${id}/`, payload);
  return data;
}

export async function deletePlan(id: string): Promise<void> {
  await api.delete(`/platform/billing/plans/${id}/`);
}

// ── Super-admin: Subscription instances ──────────────────────────────────────

export async function listSubscriptions(): Promise<HOASubscription[]> {
  const { data } = await api.get<HOASubscription[]>("/platform/billing/subscriptions/");
  return data;
}

export async function updateSubscription(
  id: string,
  payload: UpdateSubscriptionData
): Promise<HOASubscription> {
  const { data } = await api.patch<HOASubscription>(
    `/platform/billing/subscriptions/${id}/`,
    payload
  );
  return data;
}

// ── Super-admin: Subscription invoice review ─────────────────────────────────

export async function listSubscriptionInvoicesAdmin(): Promise<SubscriptionInvoice[]> {
  const { data } = await api.get<SubscriptionInvoice[]>(
    "/platform/billing/subscription-invoices/"
  );
  return data;
}

export async function verifySubscriptionInvoice(id: string): Promise<SubscriptionInvoice> {
  const { data } = await api.post<SubscriptionInvoice>(
    `/platform/billing/subscription-invoices/${id}/verify/`
  );
  return data;
}

export async function rejectSubscriptionInvoice(
  id: string,
  reason: string
): Promise<SubscriptionInvoice> {
  const { data } = await api.post<SubscriptionInvoice>(
    `/platform/billing/subscription-invoices/${id}/reject/`,
    { reason }
  );
  return data;
}

// ── HOA Admin: self-service ───────────────────────────────────────────────────

export async function listAvailablePlans(): Promise<PublicSubscriptionPlan[]> {
  const { data } = await api.get<PublicSubscriptionPlan[]>(
    "/platform/billing/available-plans/"
  );
  return data;
}

export async function getMySubscription(): Promise<HOASubscription> {
  const { data } = await api.get<HOASubscription>("/platform/billing/my-subscription/");
  return data;
}

export async function listMyInvoices(): Promise<SubscriptionInvoice[]> {
  const { data } = await api.get<SubscriptionInvoice[]>("/platform/billing/my-invoices/");
  return data;
}

export async function requestSubscriptionPlan(planId: string): Promise<SubscriptionInvoice> {
  const { data } = await api.post<SubscriptionInvoice>("/platform/billing/my-invoices/", {
    plan: planId,
  });
  return data;
}

export async function uploadSubscriptionProof(
  invoiceId: string,
  proofFile: File,
  paymentMethod: SubscriptionPaymentMethod
): Promise<SubscriptionInvoice> {
  const form = new FormData();
  form.append("payment_proof", proofFile);
  form.append("payment_method", paymentMethod);
  const { data } = await api.post<SubscriptionInvoice>(
    `/platform/billing/my-invoices/${invoiceId}/pay/`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}

export async function cancelSubscriptionInvoice(invoiceId: string): Promise<SubscriptionInvoice> {
  const { data } = await api.post<SubscriptionInvoice>(
    `/platform/billing/my-invoices/${invoiceId}/cancel/`
  );
  return data;
}

/* ─────────────── Platform Payment Details ─────────────── */

export type PlatformPaymentInfo = {
  bank_name: string;
  bank_account_name: string;
  bank_account_number: string;
  bank_routing_number: string;
  bank_notes: string;
  zelle_phone: string;
  zelle_email: string;
  zelle_name: string;
  paypal_email: string;
  pago_movil_bank?: string;
  pago_movil_phone?: string;
  pago_movil_id_number?: string;
  cash_instructions?: string;
  payment_instructions: string;
  updated_at: string;
};

export async function getPlatformPaymentInfo(): Promise<PlatformPaymentInfo> {
  const { data } = await api.get<PlatformPaymentInfo>("/platform/billing/payment-details/");
  return data;
}

export async function updatePlatformPaymentInfo(
  payload: Partial<Omit<PlatformPaymentInfo, "updated_at">>
): Promise<PlatformPaymentInfo> {
  const { data } = await api.put<PlatformPaymentInfo>("/platform/billing/payment-details/", payload);
  return data;
}
